"use client";

import React, { useState, ChangeEvent, FormEvent, useRef } from "react";
import { Phone, MapPin, Clock, Send, Star } from "lucide-react";
import { FaFacebookF, FaInstagram, FaGoogle } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

interface FormData {
  first: string;
  last: string;
  email: string;
  phone: string;
  description: string;
  howHeard: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    first: "",
    last: "",
    email: "",
    phone: "",
    description: "",
    howHeard: "",
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    const { first, last, email, phone, description } = formData;

    if (!first.trim()) {
      setErrorMessage("First name is required");
      return false;
    }
    if (!last.trim()) {
      setErrorMessage("Last name is required");
      return false;
    }
    
    // Check if at least one contact method is provided
    const hasEmail = email.trim();
    const hasPhone = phone.trim();
    
    if (!hasEmail && !hasPhone) {
      setErrorMessage("Please provide either an email address or phone number");
      return false;
    }
    
    // Validate email format if provided
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Please enter a valid email address");
      return false;
    }
    
    // Validate phone format if provided
    if (hasPhone && !/^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/[\s\-\(\)\.]/g, ''))) {
      setErrorMessage("Please enter a valid phone number");
      return false;
    }

    if (!description.trim()) {
      setErrorMessage("Description is required");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset error states
    setShowError(false);
    setErrorMessage("");

    // Validate form
    if (!validateForm()) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // reCAPTCHA disabled temporarily
      // const recaptchaToken = await recaptchaRef.current?.executeAsync();
      // recaptchaRef.current?.reset();

      // if (!recaptchaToken) {
      //   setErrorMessage("Security verification failed. Please try again.");
      //   setShowError(true);
      //   setIsSubmitting(false);
      //   return;
      // }

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
        }),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ 
          first: "", 
          last: "", 
          email: "", 
          phone: "",
          description: "",
          howHeard: "",
        });

        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        const error = await response.json();
        setErrorMessage(
          error.error || "Failed to send message. Please try again."
        );
        setShowError(true);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrorMessage(
        "Network error. Please check your connection and try again."
      );
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const officeHours: Array<{ day: string; time: string }> = [
    { day: "Monday", time: "10:00 am - 7:00 pm" },
    { day: "Tuesday", time: "10:00 am - 7:00 pm" },
    { day: "Wednesday", time: "10:00 am - 7:00 pm" },
    { day: "Thursday", time: "10:00 am - 7:00 pm" },
    { day: "Friday", time: "10:00 am - 2:00 pm" },
    { day: "Saturday", time: "Closed" },
    { day: "Sunday", time: "11:00 am - 5:00 pm" },
  ];

  const referralSources = [
    { value: "", label: "How did you hear about us?" },
    { value: "google_search", label: "Google Search" },
    { value: "google_ads", label: "Google Ads" },
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "yelp", label: "Yelp" },
    { value: "friend_referral", label: "Friend/Family Referral" },
    { value: "walk_by", label: "Walked by the Office" },
    { value: "other", label: "Other" },
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 py-20 pt-32">
          <div className="max-w-7xl mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">Contact Our Astoria Dentist Today</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#AE7D2F] to-[#d5ac26] mx-auto mb-6"></div>
            <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto mb-8">
              Get in touch with our friendly team to schedule your appointment
              or ask any questions
            </p>
            <a
              href="https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-[#AE7D2F] to-[#d5ac26] text-white font-bold py-4 px-8 rounded-full hover:from-[#d5ac26] hover:to-[#AE7D2F] transform hover:scale-105 transition-all duration-300 shadow-lg text-lg"
            >
              Book Your Appointment Now
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Introduction */}
          <div className="text-center mb-16">
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Thank you for considering smile4me as your preferred dentist. Dr.
              Iosif and the amazing team at smile4me are here for you every step
              of the way. Call to schedule an appointment with Dr. Iosif in our
              Astoria, NY dental office.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Request a Callback
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] mx-auto"></div>
                <p className="text-gray-600 mt-4">
                  Provide your information and we&#39;ll call you back to discuss scheduling and answer any questions.
                </p>
              </div>

              {showSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <div>
                      <h3 className="text-green-800 font-semibold">
                        Request Submitted Successfully!
                      </h3>
                      <p className="text-green-700 text-sm">
                        Thank you for contacting us. We&#39;ll call you back within 24 hours to discuss your dental needs and schedule an appointment.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {showError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm">✕</span>
                    </div>
                    <div>
                      <h3 className="text-red-800 font-semibold">Error</h3>
                      <p className="text-red-700 text-sm">{errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="first"
                      value={formData.first}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                      placeholder="First Name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="last"
                      value={formData.last}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                      placeholder="Last Name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                    placeholder="Email Address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                    placeholder="Phone Number"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    * Please provide either an email address or phone number (or both) so we can contact you.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us?
                  </label>
                  <select
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                  >
                    {referralSources.map((source) => (
                      <option key={source.value} value={source.value}>
                        {source.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-[#d5ac26] rounded-lg focus:ring-2 focus:ring-[#AE7D2F] focus:border-transparent transition-all duration-200"
                    placeholder="Please describe what you're looking for (cleaning, consultation, specific treatment, etc.)"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] text-white font-semibold py-4 px-8 rounded-lg hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Send className="w-5 h-5 mr-2" />
                      Request Callback
                    </div>
                  )}
                </button>

                {/* reCAPTCHA */}
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
                />
              </form>
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="h-full min-h-[600px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12088.416231176912!2d-73.918023!3d40.759736!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25fc39318e9fb%3A0x71b1f0787fac3123!2ssmile4me%20dental%20care!5e0!3m2!1sen!2sin!4v1704900634959!5m2!1sen!2sin"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Smile4Me Dental Care Location"
                />
              </div>
            </div>
          </div>

          {/* Contact Information Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
            {/* Phone & Address */}
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Call Us</h3>
              <a
                href="tel:+17184004400"
                className="text-2xl font-bold text-[#d5ac26] hover:text-[#AE7D2F] transition-colors duration-200 block mb-4"
              >
                (718) 400-4400
              </a>
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-center mb-2">
                  <MapPin className="w-5 h-5 text-[#d5ac26] mr-2" />
                  <span className="font-semibold text-gray-700">Address</span>
                </div>
                <a
                  href="https://maps.google.com/maps?cid=8192593597492375843"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-[#d5ac26] transition-colors duration-200"
                >
                  31-57 Steinway St
                  <br />
                  Queens, NY 11103
                </a>
              </div>
            </div>

            {/* Office Hours */}
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Office Hours
              </h3>
              <div className="space-y-2 text-sm">
                {officeHours.map((schedule, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center"
                  >
                    <span className="font-medium text-gray-700">
                      {schedule.day}
                    </span>
                    <span
                      className={`${
                        schedule.time === "Closed"
                          ? "text-red-500"
                          : "text-gray-600"
                      }`}
                    >
                      {schedule.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social & Reviews */}
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">
                Follow Us
              </h3>
              <div className="flex justify-center space-x-4 mb-6">
                <a
                  href="https://www.facebook.com/profile.php?id=100090912068381"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#ECD05B] transition-colors duration-300"
                >
                  <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                    <FaFacebookF className="text-white text-base" />
                  </span>
                </a>
                <a
                  href="https://www.instagram.com/smile4mecare/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#ECD05B] transition-colors duration-300"
                >
                  <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                    <FaInstagram className="text-white text-base" />
                  </span>
                </a>
                <a
                  href="https://maps.app.goo.gl/GcexFskLt3MwefMR9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#ECD05B] transition-colors duration-300"
                >
                  <span className="bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] rounded-full p-2.5 inline-flex items-center justify-center">
                    <FaGoogle className="text-white text-base" />
                  </span>
                </a>
              </div>
              <p className="text-gray-600 text-sm">
                Connect with us on social media and read our patient reviews!
              </p>
            </div>
          </div>

          {/* Book Appointment CTA */}
          <div className="mt-16 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Smile?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              We offer zero interest payment plans over 24 months. Come see why
              Astoria, Queens, Long Island City LOVES Dr. Iosif & smile4me!
            </p>
            <a
              href="https://app.nexhealth.com/appt/smile4me_dental_care?lid=77847&tracking_key=b2d71902-5ce0-43c1-bc95-24cdee27de9a"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-[#AE7D2F] to-[#d5ac26] text-white font-bold py-4 px-8 rounded-full hover:from-[#d5ac26] hover:to-[#AE7D2F] transform hover:scale-105 transition-all duration-300 shadow-lg text-lg"
            >
              Book Your Appointment Now
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;